import { useState, useRef, useEffect } from 'react';
import { Card, Group, Stack, Text, TextInput, Button, ScrollArea, Center } from '@mantine/core';
import { IconSend, IconLifebuoy } from '@tabler/icons-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import { PageHeader } from '../components/PageHeader';
import { useTranslation } from 'react-i18next';

interface Msg { id: string; fromSuperadmin: boolean; body: string; createdAt: string }

/** Tenant admini — platforma superadmini bilan yozishma (yordam so'rash). */
export default function PlatformSupport() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const viewportRef = useRef<HTMLDivElement>(null);

  const { data } = useQuery({
    queryKey: ['platform-support'],
    queryFn: async () => (await api.get<{ messages: Msg[] }>('/platform-support')).data,
    refetchInterval: 15_000,
  });
  const messages = data?.messages ?? [];

  const sendMut = useMutation({
    mutationFn: (body: string) => api.post('/platform-support', { body }),
    onSuccess: () => { setText(''); qc.invalidateQueries({ queryKey: ['platform-support'] }); },
  });

  useEffect(() => { viewportRef.current?.scrollTo({ top: viewportRef.current.scrollHeight }); }, [messages.length]);

  return (
    <>
      <PageHeader title={t('nav.platformSupport')} subtitle={t('platformSupportPage.subtitle')} />
      <Card style={{ display: 'flex', flexDirection: 'column', minHeight: 480, maxWidth: 720 }}>
        <ScrollArea style={{ flex: 1 }} viewportRef={viewportRef} mb="sm">
          {!messages.length ? (
            <Center h={360}>
              <Stack align="center" gap="xs">
                <IconLifebuoy size={40} color="var(--mantine-color-gray-5)" />
                <Text c="dimmed" size="sm" ta="center" maw={320}>{t('platformSupportPage.emptyHint')}</Text>
              </Stack>
            </Center>
          ) : (
            <Stack gap="xs" p="xs">
              {messages.map((m) => (
                <Card key={m.id} p="xs" maw="75%" style={{ alignSelf: m.fromSuperadmin ? 'flex-start' : 'flex-end', background: m.fromSuperadmin ? 'var(--mantine-color-gray-1)' : 'var(--mantine-color-brand-1)' }}>
                  <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{m.body}</Text>
                  <Text size="xs" c="dimmed" ta="right">{new Date(m.createdAt).toLocaleString('uz-UZ', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</Text>
                </Card>
              ))}
            </Stack>
          )}
        </ScrollArea>
        <Group gap="xs">
          <TextInput style={{ flex: 1 }} placeholder={t('platformSupportPage.messagePlaceholder')} value={text} onChange={(e) => setText(e.currentTarget.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && text.trim()) sendMut.mutate(text.trim()); }} />
          <Button leftSection={<IconSend size={16} />} loading={sendMut.isPending} disabled={!text.trim()} onClick={() => sendMut.mutate(text.trim())}>{t('platformSupportPage.send')}</Button>
        </Group>
      </Card>
    </>
  );
}
